import React, { useState } from 'react';
import './LibraryCatalog.css';

const LibraryCatalog = ({ books, onBookSelect, onSearch, searchQuery }) => {
  const [filter, setFilter] = useState('all'); // 'all', 'available', 'borrowed', 'overdue'
  const [sortBy, setSortBy] = useState('title'); // 'title', 'author', 'callNumber', 'addedDate'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(localSearchQuery);
  };

  const getStatusText = (book) => {
    switch (book.status) {
      case 'available':
        return '可借';
      case 'borrowed':
        return '已借出';
      case 'overdue':
        return '逾期';
      case 'reserved':
        return '已預約';
      case 'maintenance':
        return '維護中';
      default:
        return '未知';
    }
  };

  const getStatusClass = (book) => {
    switch (book.status) {
      case 'available':
        return 'status-available';
      case 'borrowed':
        return 'status-borrowed';
      case 'overdue':
        return 'status-overdue';
      case 'reserved':
        return 'status-reserved';
      case 'maintenance':
        return 'status-maintenance';
      default:
        return 'status-unknown';
    }
  };

  const filteredAndSortedBooks = books
    .filter(book => {
      // Filter by status
      if (filter === 'available' && book.status !== 'available') return false;
      if (filter === 'borrowed' && book.status !== 'borrowed') return false;
      if (filter === 'overdue' && book.status !== 'overdue') return false;

      // Filter by search query
      if (localSearchQuery) {
        const query = localSearchQuery.toLowerCase();
        return (
          (book.title && book.title.toLowerCase().includes(query)) ||
          (book.titleEn && book.titleEn.toLowerCase().includes(query)) ||
          (book.author && book.author.toLowerCase().includes(query)) ||
          (book.authorEn && book.authorEn.toLowerCase().includes(query)) ||
          (book.isbn && book.isbn.includes(query)) ||
          (book.call_number && book.call_number.toLowerCase().includes(query)) ||
          (book.callNumber && book.callNumber.toLowerCase().includes(query)) ||
          (book.subject && book.subject.toLowerCase().includes(query))
        );
      }

      return true;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'author':
          aValue = (a.author || '').toLowerCase();
          bValue = (b.author || '').toLowerCase();
          break;
        case 'callNumber':
          aValue = (a.call_number || a.callNumber || '').toLowerCase();
          bValue = (b.call_number || b.callNumber || '').toLowerCase();
          break;
        case 'addedDate':
          aValue = new Date(a.created_at || a.addedDate || 0);
          bValue = new Date(b.created_at || b.addedDate || 0);
          break;
        default:
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="library-catalog">
      <div className="catalog-header">
        <h2>圖書目錄</h2>
        <div className="catalog-stats">
          <span>總藏書: {books.length}</span>
          <span>可借: {books.filter(b => b.status === 'available').length}</span>
          <span>已借出: {books.filter(b => b.status === 'borrowed').length}</span>
        </div>
      </div>

      <div className="catalog-controls">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="搜尋書名、作者、ISBN、索書號..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            搜尋
          </button>
        </form>

        <div className="filter-controls">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">全部狀態</option>
            <option value="available">可借</option>
            <option value="borrowed">已借出</option>
            <option value="overdue">逾期</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => handleSort(e.target.value)}
            className="sort-select"
          >
            <option value="title">按書名排序</option>
            <option value="author">按作者排序</option>
            <option value="callNumber">按索書號排序</option>
            <option value="addedDate">按新增日期排序</option>
          </select>
        </div>
      </div>

      <div className="catalog-table">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('callNumber')}>
                索書號 {getSortIcon('callNumber')}
              </th>
              <th onClick={() => handleSort('title')}>
                書名 {getSortIcon('title')}
              </th>
              <th onClick={() => handleSort('author')}>
                作者 {getSortIcon('author')}
              </th>
              <th>出版社</th>
              <th>出版年份</th>
              <th>狀態</th>
              <th>位置</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedBooks.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-results">
                  沒有找到符合條件的圖書
                </td>
              </tr>
            ) : (
              filteredAndSortedBooks.map((book) => (
                <tr key={book.id} className="book-row">
                  <td className="call-number">{book.call_number || book.callNumber || 'N/A'}</td>
                  <td className="book-title">
                    <div className="title-content">
                      <span className="title-zh">{book.title || 'N/A'}</span>
                      {book.titleEn && (
                        <span className="title-en">{book.titleEn}</span>
                      )}
                    </div>
                  </td>
                  <td className="book-author">
                    <div className="author-content">
                      <span className="author-zh">{book.author || 'N/A'}</span>
                      {book.authorEn && (
                        <span className="author-en">{book.authorEn}</span>
                      )}
                    </div>
                  </td>
                  <td className="publisher">{book.publisher || 'N/A'}</td>
                  <td className="publish-year">{book.publication_year || book.publishYear || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(book)}`}>
                      {getStatusText(book)}
                    </span>
                  </td>
                  <td className="location">{book.location || 'N/A'}</td>
                  <td className="actions">
                    <button
                      className="view-button"
                      onClick={() => onBookSelect(book)}
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="catalog-footer">
        <p>顯示 {filteredAndSortedBooks.length} 本圖書</p>
        {localSearchQuery && (
          <p>搜尋結果: "{localSearchQuery}"</p>
        )}
      </div>
    </div>
  );
};

export default LibraryCatalog;
